import type { BusinessSearchResult } from '../../api/services/lupp/order/dto.order';
import type { DunsBusinessEntity } from '../../api/services/lupp/quotes/dto.quotes';
import { parseLocalePrice } from '../../utils/tools';

type BusinessFieldMap = {
  name: string;
  duns: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
};

export const getBusinessSelectionFieldValues = (business: BusinessSearchResult | null) => {
  const countryValue = business?.country || business?.countryCode || '';

  return {
    name: business?.name ?? '',
    duns: business?.duns ?? '',
    address: business?.address ?? '',
    postalCode: business?.postalCode ?? '',
    city: business?.city ?? '',
    country: countryValue,
  };
};

export const applyBusinessSelection = (
  business: BusinessSearchResult | null,
  setValue: (name: string, value: unknown) => void,
  fieldMap: BusinessFieldMap
) => {
  const values = getBusinessSelectionFieldValues(business);

  setValue(fieldMap.name, values.name);
  setValue(fieldMap.duns, values.duns);
  setValue(fieldMap.address, values.address);
  setValue(fieldMap.postalCode, values.postalCode);
  setValue(fieldMap.city, values.city);
  setValue(fieldMap.country, values.country);
};

/**
 * Calculates discounted total from base price and discount values
 * @param baseTotal - The original total amount
 * @param cashDiscount - Cash discount value (optional)
 * @param percentageDiscount - Percentage discount value (optional)
 * @returns Discounted total (never negative)
 */
export const calculateDiscountedTotal = (
  baseTotal: number,
  cashDiscount?: string,
  percentageDiscount?: string
): number => {
  const cashDiscountValue = parseLocalePrice(cashDiscount);
  const percentageDiscountValue = parseLocalePrice(percentageDiscount);

  let discountedTotal = baseTotal;
  if (cashDiscountValue > 0) {
    discountedTotal = baseTotal - cashDiscountValue;
  } else if (percentageDiscountValue > 0) {
    discountedTotal = baseTotal - (baseTotal * percentageDiscountValue) / 100;
  }
  return Math.max(0, discountedTotal);
};

/**
 * Builds D&B business entity object with nil checks
 * @param entity - Business search result from D&B API
 * @returns DunsBusinessEntity or undefined if entity is null
 */
export const buildDunsEntity = (
  entity: BusinessSearchResult | null
): DunsBusinessEntity | undefined => {
  if (!entity) return undefined;

  return {
    dunsNumber: entity.duns || '',
    primaryName: entity.primaryName || '',
    companyName: entity.name || '',
    isOutOfBusiness: entity.isOutOfBusiness ?? false,
  };
};

/**
 * Builds D&B business entity from selected entity, or falls back to form DUNS values.
 * This preserves DUNS on quote updates when no new business selection event is fired.
 */
const buildDunsEntityFromSelectionOrFallback = (
  selectedEntity: BusinessSearchResult | null,
  dunsNumber?: string,
  companyName?: string
): DunsBusinessEntity | undefined => {
  const selectedDunsEntity = buildDunsEntity(selectedEntity);
  if (selectedDunsEntity) return selectedDunsEntity;

  if (!dunsNumber) return undefined;

  return {
    dunsNumber,
    primaryName: companyName ?? '',
    companyName: companyName ?? '',
    isOutOfBusiness: false,
  };
};

/**
 * Builds business information section for quote payload
 * @param formData - Form data containing business details
 * @param selectedEntity - Selected business entity from D&B search
 * @returns Business information object or empty object if not Business prospect
 */
export const buildBusinessInfo = (
  formData: {
    prospectType: string;
    companyName?: string;
    companyDuns?: string;
    companyTaxId?: string;
    invoiceAddress?: string;
    businessCity?: string;
    invoiceCountry?: string;
    businessPostalCode?: string;
  },
  selectedEntity: BusinessSearchResult | null
) => {
  if (formData.prospectType !== 'Business') return {};

  const dunsEntity = buildDunsEntityFromSelectionOrFallback(
    selectedEntity,
    formData.companyDuns,
    formData.companyName
  );

  return {
    companyName: formData.companyName,
    ...(formData.companyTaxId ? { companyTaxIdentification: formData.companyTaxId } : {}),
    invoiceAddressLine1: formData.invoiceAddress,
    invoiceCity: formData.businessCity,
    invoiceCountry: formData.invoiceCountry,
    invoicePostalCode: formData.businessPostalCode,
    ...(dunsEntity && { duns: dunsEntity }),
  };
};

/**
 * Builds financing details section for quote payload
 * @param formData - Form data containing finance company details
 * @param selectedEntity - Selected finance company entity from D&B search
 * @returns Financing details object or empty object if not 3rd Party Financing
 */
export const buildFinancingDetails = (
  formData: {
    preferredPaymentMethod: string;
    financeCompanyName?: string;
    financeDuns?: string;
    financeCompanyAddress?: string;
    financeCity?: string;
    financeCountry?: string;
    financePostalCode?: string;
    financeVat?: string;
  },
  selectedEntity: BusinessSearchResult | null
) => {
  if (formData.preferredPaymentMethod !== '3rd Party Financing') return {};

  const dunsEntity = buildDunsEntityFromSelectionOrFallback(
    selectedEntity,
    formData.financeDuns,
    formData.financeCompanyName
  );

  return {
    financingDetails: {
      financeCompanyName: formData.financeCompanyName,
      financeCompanyAddress: formData.financeCompanyAddress,
      financeCity: formData.financeCity,
      financeCountry: formData.financeCountry,
      financePostalCode: formData.financePostalCode,
      financeVat: formData.financeVat,
      ...(dunsEntity && { duns: dunsEntity }),
    },
  };
};
